type Handler = (payload: unknown) => void

const handlers = new Map<string, Set<Handler>>()
const reconnectHandlers = new Set<() => void>()

/**
 * Features register their own handlers; the transport knows no feature. A
 * single dispatcher that imports every feature and switches over all event
 * types would violate the dependency rules and grow without bound.
 */
export function onStreamEvent(type: string, handler: Handler): () => void {
  const existing = handlers.get(type) ?? new Set<Handler>()
  existing.add(handler)
  handlers.set(type, existing)

  return () => {
    existing.delete(handler)
    if (existing.size === 0) handlers.delete(type)
  }
}

/**
 * Events that happened while the socket was down are gone. Every feature that
 * owns stream data must refetch its snapshot here, or its table quietly
 * diverges from reality.
 */
export function onStreamReconnect(handler: () => void): () => void {
  reconnectHandlers.add(handler)
  return () => {
    reconnectHandlers.delete(handler)
  }
}

export function dispatchStreamEvent(type: string, payload: unknown): void {
  const listeners = handlers.get(type)
  if (listeners === undefined) return

  for (const handler of listeners) {
    try {
      handler(payload)
    } catch (error) {
      // Log the event name only. Payloads carry personal and card data and
      // must never reach the console or a log collector.
      console.error(`stream handler failed for "${type}"`, error)
    }
  }
}

export function dispatchStreamReconnect(): void {
  for (const handler of reconnectHandlers) handler()
}

/** Subscription bookkeeping lives here, with one explicit reset. */
export function clearStreamHandlers(): void {
  handlers.clear()
  reconnectHandlers.clear()
}
